// WHAT: Trend Card Component (Molecule)
// WHY:  Displays health metric trends with direction indicators and color coding
// DOMAIN: Health → Withings
// DEPENDENCIES: Lucide icons, UI Card and Badge components, TrendResult type
// RISKS:
//   - NOTE: Trend data may be null (shows "No data" state)
//   - NOTE: Color and direction mapping depends on TrendResult structure
//   - NOTE: Badge variant mapping may not cover all trend colors

import type { TrendResult } from "../../services/trendAnalysisService";

import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import React from "react";

import { Card } from "@/components/ui";
import { Badge } from "@/components/ui/Badge";

interface TrendCardProperties {
  className?: string;
  currentValue: null | number;
  title: string;
  trend: null | TrendResult;
  unit: string;
}

/**
 * Maps color string to Tailwind CSS color classes
 *
 * @param color - Color name (green, red, yellow)
 *
 * @returns {string} Tailwind CSS class name
 *
 */
const getColorClasses = (color: string): string => {
  switch (color) {
    case "green": {
      return "text-green-400";
    }
    case "red": {
      return "text-red-400";
    }
    case "yellow": {
      return "text-yellow-400";
    }
    default: {
      return "text-slate-400";
    }
  }
};

/**
 * Maps color string to Tailwind CSS icon color classes
 *
 * @param color - Color name (green, red, yellow)
 *
 * @returns {string} Tailwind CSS class name
 *
 */
const getIconColor = (color: string): string => {
  switch (color) {
    case "green": {
      return "text-green-500";
    }
    case "red": {
      return "text-red-500";
    }
    case "yellow": {
      return "text-yellow-400";
    }
    default: {
      return "text-slate-500";
    }
  }
};

/**
 * Maps trend color to badge variant
 *
 * @param color - Color name (green, red, yellow)
 *
 * @returns {"negative" | "neutral" | "positive"} Badge variant
 *
 */
const getBadgeVariant = (color: string): "default" | "destructive" | "success" => {
  if (color === "green") {
    return "success";
  }
  if (color === "red") {
    return "destructive";
  }
  return "default";
};

/**
 * WHAT:
 *   Card component displaying health metric trends with visual indicators.
 *
 * WHY:
 *   Provides clear trend visualization for health metrics (weight, BMI, etc.)
 *   with direction, color coding, and descriptive labels.
 *
 * PROPS:
 *   - title: string — Metric name (e.g., "Weight", "BMI")
 *   - trend: TrendResult | null — Trend analysis result (null shows empty state)
 *   - currentValue: number | null — Current metric value
 *   - unit: string — Unit of measurement (e.g., "kg", "bpm")
 *   - className?: string — Additional CSS classes
 *
 * BEHAVIOR:
 *   - Shows "No data" state if trend is null
 *   - Displays current value with unit if provided
 *   - Shows trend percentage with +/- prefix and color coding
 *   - Displays trend description text
 *   - Shows direction icon (up/down/neutral) with matching color
 *   - Displays trend label as Badge with variant based on color
 *   - Color coding: green (positive), red (negative), yellow (neutral)
 *
 * RISKS:
 *   - NOTE: Trend data structure must match TrendResult interface
 *   - NOTE: Badge variant mapping may not support all trend colors
 *   - NOTE: Empty state doesn't show current value even if provided
 *
 * @param root0
 *
 * @param root0.title
 *
 * @param root0.trend
 *
 * @param root0.currentValue
 *
 * @param root0.unit
 *
 * @param root0.className
 *
 * @returns {JSX.Element} Trend card component with health metric trends
 *
 */
export const TrendCard: React.FC<TrendCardProperties> = ({
  className = "",
  currentValue,
  title,
  trend,
  unit,
}) => {
  if (!trend) {
    return (
      <Card className={`bg-slate-800/50 border-slate-700 text-white ${className}`}>
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{title}</p>
              <p className="text-lg font-semibold text-slate-500">No data</p>
            </div>
            <Minus className="h-6 w-6 text-slate-500" />
          </div>
        </div>
      </Card>
    );
  }

  const getIcon = (direction: string) => {
    switch (direction) {
      case "up": {
        return <TrendingUp className="h-6 w-6" />;
      }
      case "down": {
        return <TrendingDown className="h-6 w-6" />;
      }
      case "neutral": {
        return <Minus className="h-6 w-6" />;
      }
      default: {
        return <Minus className="h-6 w-6" />;
      }
    }
  };

  return (
    <Card className={`bg-slate-800/50 border-slate-700 text-white ${className}`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-slate-400 text-sm mb-1">{title}</p>

            {/* Aktueller Wert */}
            {currentValue !== null && (
              <p className="text-lg font-semibold text-white mb-1">
                {currentValue.toFixed(2)} {unit}
              </p>
            )}

            {/* Trend-Wert */}
            <p className={`text-xl font-bold ${getColorClasses(trend.color)}`}>
              {trend.direction === "up" ? "+" : ""}
              {trend.value.toFixed(1)}%
            </p>

            {/* Trend-Beschreibung */}
            <p className="text-sm text-slate-400 mt-1">{trend.description}</p>
          </div>

          {/* Trend-Icon */}
          <div className={`ml-4 ${getIconColor(trend.color)}`}>{getIcon(trend.direction)}</div>
        </div>

        {/* Trend-Label */}
        <div className="mt-3">
          <Badge className="text-xs" variant={getBadgeVariant(trend.color)}>
            {trend.label}
          </Badge>
        </div>
      </div>
    </Card>
  );
};
